import { dedent } from 'ts-dedent';
import { DataWarehouseConnection } from '..';
import { ReadTable } from '../../../components/readtable';
import { ColumnDataType, Provider, TableOptimizationColumnDataType, WorkflowCodeType, WorkflowComponentParamType } from '../../../types';
import { OUTPUT_TABLE_FQN_VAR_NAME, OUTPUT_TABLE_NAME_VAR_NAME, WORKFLOW_OUTPUT_TABLE_NAME_PARAMETER_NAME, formatVariableValue, getVariablesUsedInQuery, hashString, isNumericIntegerValue } from '../../../utils';
export class RSConnection extends DataWarehouseConnection {
    constructor(name, tempStoragePath) {
        super(name, tempStoragePath, Provider.Redshift);
    }
    castValue(value, type) {
        return `(${value})::${type}`;
    }
    getTempSelectQuery(select, dryRun) {
        if (dryRun) {
            return dedent `SELECT * FROM
                (
                  ${select}
                )
              WHERE 1=0`;
        }
        else {
            return select;
        }
    }
    getCreateTempTableQuery(tablename, select, codeType, optimizeBy, addOptimizationStats) {
        const dryRun = codeType === WorkflowCodeType.DryRun;
        const temp = codeType === WorkflowCodeType.StoredProcedure ? 'TEMPORARY ' : '';
        select = dedent(select);
        tablename = this.quoteName(tablename);
        if (!dryRun &&
            optimizeBy?.column &&
            TableOptimizationColumnDataType.Geo !== optimizeBy.column.type) {
            return dedent `
                DROP TABLE IF EXISTS ${tablename};
                CREATE ${temp}TABLE ${tablename}
                AS
                  ${this.getTempSelectQuery(select, dryRun)};
                ALTER TABLE ${tablename} ALTER SORTKEY
                  (
                    ${optimizeBy.column.name}
                  );`;
        }
        else {
            return dedent `
              DROP TABLE IF EXISTS ${tablename};
              CREATE ${temp}TABLE ${tablename}
                AS
                  ${this.getTempSelectQuery(select, dryRun)};`;
        }
    }
    getCreateTableQuery(tablename, select, codeType, optimizeBy, addOptimizationStats) {
        return this.getCreateTempTableQuery(tablename, select, codeType, optimizeBy, addOptimizationStats).replace(RegExp('TEMPORARY ', 'g'), '');
    }
    getCreateViewQuery(viewname, select, codeType) {
        select = dedent(select);
        viewname = this.quoteName(viewname);
        return dedent `
              DROP VIEW IF EXISTS ${viewname};
              CREATE VIEW ${viewname} AS
                ${this.getTempSelectQuery(select, codeType === WorkflowCodeType.DryRun)};`;
    }
    getCreateTempViewQuery(tablename, select, codeType) {
        return this.getCreateViewQuery(tablename, select, codeType);
    }
    getOutputTableName(workflowId, componentName, params, output, codeType) {
        return super
            .getOutputTableName(workflowId, componentName, params, output, codeType)
            .toLowerCase();
    }
    wrapComponentCodeInFlowControlBlock(query, codeType) {
        return dedent `BEGIN
              ${query}
            END;`;
    }
    getStoredProcedureOutputTableNameExpression(workflow, full) {
        const publicVars = workflow.variables.filter((v) => v.public);
        let root = workflow.getStoredProcedureOutputTableFullName(this);
        if (!full) {
            root = root.split('.').pop();
        }
        if (publicVars.length === 0) {
            return `'${root}_' || SUBSTRING(MD5(''), 1, 16)`;
        }
        else {
            return `'${root}_' || SUBSTRING(MD5(${publicVars
                .map((v) => this.quoteVariableName(v.name))
                .join(" || ',' || ")}), 1, 16)`;
        }
    }
    wrapCodeInIfTableExistsBlock(query, outputTables, codeType, outputTablesExpressions, variablePrefix) {
        variablePrefix = variablePrefix || '';
        if ([WorkflowCodeType.DryRun, WorkflowCodeType.StoredProcedure].includes(codeType)) {
            return query;
        }
        else if (codeType === WorkflowCodeType.FullNoCache) {
            return dedent `
        ${outputTables
                .map((x) => `DROP TABLE IF EXISTS ${this.quoteName(x)};`)
                .join('\n')}
        ${query}
      `;
        }
        else {
            const [database, schema] = this.tempStoragePath.toLowerCase().split('.');
            const expressionChecks = [];
            for (const expression of outputTablesExpressions || []) {
                expressionChecks.push(dedent `
            ${variablePrefix}schema := SPLIT_PART(${expression}, '.', 2);
            ${variablePrefix}tableName := SPLIT_PART(${expression}, '.', 3);
            EXECUTE
              'SELECT
                COUNT(1) AS cnt
              FROM
                "${database}".information_schema.tables
              WHERE
                  table_name LIKE ''' || ${variablePrefix}tableName || '''
                  AND table_schema LIKE ''' || ${variablePrefix}schema || ''''
              INTO ${variablePrefix}tablesCount;
            ${variablePrefix}totalTablesCount := ${variablePrefix}tablesCount + ${variablePrefix}totalTablesCount;
        `);
            }
            const outputTablesLiteralNames = outputTables.map((x) => `''${x.split('.').pop().toLowerCase()}''`);
            let literalNamesCheck = '';
            if (outputTablesLiteralNames.length > 0) {
                literalNamesCheck = dedent `
          ${variablePrefix}tablesNames := ' ${outputTablesLiteralNames.join(',')} ' ;
          ${variablePrefix}query = '
            SELECT COUNT(1) AS cnt
            FROM "${database}".information_schema.tables
            WHERE
              table_name IN (' || ${variablePrefix}tablesNames || ')
              AND table_schema LIKE ''${schema}''
          ';
          EXECUTE ${variablePrefix}query INTO ${variablePrefix}tablesCount;
          ${variablePrefix}totalTablesCount := ${variablePrefix}tablesCount + ${variablePrefix}totalTablesCount;
        `;
            }
            const totalTablesCount = outputTables.length + (outputTablesExpressions?.length || 0);
            return dedent `
          DECLARE
            ${variablePrefix}query VARCHAR(MAX);
            ${variablePrefix}tablesCount INTEGER;
            ${variablePrefix}totalTablesCount INTEGER DEFAULT 0;
            ${variablePrefix}tablesNames VARCHAR(MAX);
            ${variablePrefix}schema VARCHAR(MAX);
            ${variablePrefix}tableName VARCHAR(MAX);
          BEGIN
            ${expressionChecks.join('\n')}
            ${literalNamesCheck}
            IF (${variablePrefix}totalTablesCount < ${totalTablesCount}) THEN
              BEGIN
                ${query}
              END;
            END IF;
          END;`;
        }
    }
    async getWorkflowCode(workflow, envVars, codeType, schemas) {
        const queries = await workflow.getSubqueries(this, envVars, codeType, schemas);
        const executable = queries.some((q) => q.query);
        let query = '';
        if (executable) {
            const procName = `_temp_${hashString(JSON.stringify(workflow.componentValues))}`;
            if (codeType !== WorkflowCodeType.StoredProcedure) {
                query = queries.map((q) => dedent(q.query)).join('\n\n');
                const type = codeType === WorkflowCodeType.DryRun ? 'DRY-RUN' : 'RUN';
                query = dedent `
                /*
                ${workflow.workflowMetadataAsCommentString(codeType)}
                */
                CREATE OR REPLACE PROCEDURE ${procName}()
                AS $$
                BEGIN
                  ${query.trim()}
                END;
                $$ LANGUAGE plpgsql;

                CALL ${procName}();
                DROP PROCEDURE ${procName}();`;
                query = this.replaceVariablesInScriptQuery(query, workflow.variables);
            }
            else {
                query = this._getProcedureBody(workflow, queries, schemas);
            }
        }
        return query;
    }
    replaceVariablesInStoredProcedureQuery(variables, query, requiresDynamicSQL) {
        const varnames = getVariablesUsedInQuery(query, variables);
        if (varnames.length === 0) {
            return query;
        }
        if (requiresDynamicSQL) {
            query = query.replace(/'/g, "\\'").replace(/BEGIN\n([\s\S]*?)END;/, '$1');
            for (const varname of varnames) {
                const isPublic = variables.find((v) => v.name === varname)?.public || false;
                query = query.replace(RegExp(`@${varname}`, 'g'), `' || ${isPublic ? `__${varname}` : this.quoteVariableName(varname)} || '`);
            }
            query = this.executeDynamicSQL({ sql: query, addQuotes: true });
        }
        else {
            for (const varname of varnames) {
                query = query.replace(RegExp(`@${varname}`, 'g'), this.quoteVariableName(varname));
            }
        }
        return query;
    }
    paramTypeToDataProviderType(inputType, inputValue) {
        switch (inputType) {
            case WorkflowComponentParamType.String:
                return 'VARCHAR(MAX)';
            case WorkflowComponentParamType.Number:
                return isNumericIntegerValue(inputValue) ? 'INT' : 'FLOAT';
            case WorkflowComponentParamType.Boolean:
                return 'BOOLEAN';
            default:
                return 'VARCHAR(MAX)';
        }
    }
    columnTypeToDataProviderType(colType) {
        switch (colType) {
            case ColumnDataType.String:
                return 'VARCHAR(MAX)';
            case ColumnDataType.Number:
                return 'FLOAT';
            case ColumnDataType.Boolean:
                return 'BOOLEAN';
            case ColumnDataType.Geometry:
                return 'GEOMETRY';
            default:
                return 'VARCHAR(MAX)';
        }
    }
    wrapCodeInProcedure(name, workflow, body, includeDiagram, isImmutable) {
        const hasOutput = workflow.hasStoredProcedureOutputTable();
        const procedureParams = workflow.variables
            .filter((v) => v.public)
            .map((input) => `${this.quoteVariableName(input.name)} ${this.paramTypeToDataProviderType(input.type, input.value)}`);
        if (hasOutput) {
            procedureParams.push(`${WORKFLOW_OUTPUT_TABLE_NAME_PARAMETER_NAME} INOUT VARCHAR(MAX)`);
        }
        const declarePrivateVariables = workflow.variables
            .filter((v) => !v.public)
            .map((input) => `${this.quoteVariableName(input.name)} ${this.paramTypeToDataProviderType(input.type, input.value)} DEFAULT ${formatVariableValue(input)};`);
        const declarePublicVariables = workflow.variables
            .filter((v) => v.public)
            .map((input) => `__${input.name} ${this.paramTypeToDataProviderType(input.type, input.value)} DEFAULT COALESCE(${this.quoteVariableName(input.name)}, ${formatVariableValue(input)});`);
        const declareVariables = declarePrivateVariables.concat(declarePublicVariables);
        let outputTableVars = '';
        if (hasOutput || isImmutable) {
            declareVariables.push(`${OUTPUT_TABLE_NAME_VAR_NAME} VARCHAR(MAX);`);
            declareVariables.push(`${OUTPUT_TABLE_FQN_VAR_NAME} VARCHAR(MAX);`);
            const outputTableNameVariable = `${OUTPUT_TABLE_NAME_VAR_NAME} := ${this.getStoredProcedureOutputTableNameExpression(workflow, false)};`;
            const outputTableFqnVariable = `${OUTPUT_TABLE_FQN_VAR_NAME} := ${this.getStoredProcedureOutputTableNameExpression(workflow, true)};`;
            outputTableVars = dedent `${outputTableNameVariable}
                               ${outputTableFqnVariable}`;
        }
        const comment = this._getStoredProcedureEmbeddedComment(workflow, isImmutable);
        const returnTableName = hasOutput
            ? dedent `
    ${WORKFLOW_OUTPUT_TABLE_NAME_PARAMETER_NAME} := ${OUTPUT_TABLE_FQN_VAR_NAME};`
            : '';
        return dedent `
                CREATE OR REPLACE PROCEDURE
                  ${this.tempStoragePath}.${name}(
                    ${procedureParams.join(',\n')}
                  )
                  LANGUAGE plpgsql
                  AS
                  $$
                    ${includeDiagram ? comment : ''}
                  ${declareVariables.length > 0
            ? dedent `
                        DECLARE
                          ${declareVariables.join('\n')}`
            : ''}
                  BEGIN
                    ${outputTableVars}
                    ${body}
                    ${returnTableName}
                  END;
                  $$`;
    }
    getCheckStoredProcedureExistsQueries(workflow, isImmutable) {
        const versionId = workflow.workflowVersionHash();
        const paramsId = workflow.workflowParamsHash();
        const procName = `_temp_proc_check_${hashString(workflow.id)}_${versionId}`;
        const proc = dedent `
          DECLARE
            procedureExists BOOLEAN;
            isCurrentVersion BOOLEAN;
          BEGIN
            SELECT COUNT(*) > 0 INTO procedureExists
            FROM pg_catalog.pg_proc
            JOIN pg_namespace ON pg_catalog.pg_proc.pronamespace = pg_namespace.oid
            WHERE proname = '${workflow.getStoredProcedureName()}'
                AND pg_namespace.nspname = '${this.tempStoragePath
            .split('.')
            .pop()}';
            IF (procedureExists) THEN
              SELECT TRUE
              INTO isCurrentVersion
              FROM pg_catalog.pg_proc
              JOIN pg_namespace ON pg_catalog.pg_proc.pronamespace = pg_namespace.oid
              WHERE proname = '${workflow.getStoredProcedureName()}'
                  AND pg_namespace.nspname = '${this.tempStoragePath
            .split('.')
            .pop()}'
                  AND
                  position('"versionId":"${versionId}"' in prosrc) > 0
                  AND
                  position('"paramsId":"${paramsId}"' in prosrc) > 0
                  AND
                  position('"isImmutable":${isImmutable}' in prosrc) > 0;
              IF (isCurrentVersion) THEN
                SELECT 'exists' INTO status;
              ELSE
                SELECT 'outdated' INTO status;
              END IF;
            ELSE
              SELECT 'missing' INTO status;
            END IF;
          END;
        `;
        return [
            dedent `CREATE OR REPLACE PROCEDURE ${procName}(status OUT VARCHAR(MAX))
       AS $$
         ${proc}
       $$ LANGUAGE plpgsql;`,
            `CALL ${procName}();`,
            `DROP PROCEDURE ${procName}();`
        ];
    }
    getRemoveStoredProcedureQuery(workflow) {
        const procedureParams = workflow.variables
            .filter((v) => v.public)
            .map((input) => `${this.paramTypeToDataProviderType(input.type, input.value)}`);
        if (workflow.hasStoredProcedureOutputTable()) {
            // Param _outputtablename INOUT TEXT
            procedureParams.push('TEXT');
        }
        return dedent `DROP PROCEDURE ${this.getStoredProcedureFullName(workflow)}(${procedureParams.join()})`;
    }
    extractErrorLineNumberFromApiResponse(response) {
        const match = response.match(/line (\d+)/);
        if (match) {
            const OFFSET = 7; // offset caused by header comment and procedure definition
            return parseInt(match[1]) + OFFSET;
        }
        else {
            return null;
        }
    }
    getSourceTablePermissionsQuery(workflow) {
        const conditions = {};
        const tablenames = [];
        for (const [componentName, component] of Object.entries(workflow.components)) {
            if (component.metadata.name === new ReadTable().metadata.name) {
                const value = workflow.componentValues[componentName]['source'];
                const tablename = value.value;
                tablenames.push(tablename.replace(/"/g, ''));
                const [catalog, schema, name] = tablename.toLowerCase().split('.');
                if (!(catalog in conditions)) {
                    conditions[catalog] = [];
                }
                conditions[catalog].push(dedent `(schemaname = '${schema}'
            AND tablename = '${name}'
            AND HAS_TABLE_PRIVILEGE(CURRENT_USER, table_name, 'select'))`);
                tablename;
            }
        }
        if (tablenames.length === 0) {
            return '';
        }
        const accessible = Object.keys(conditions)
            .map((k) => {
            return dedent `SELECT CURRENT_DATABASE() || '.' || schemaname || '.' || tablename AS table_name
                FROM pg_tables
                WHERE ${conditions[k].join('\nOR\n')}`;
        })
            .join('\nUNION ALL\n');
        const query = dedent `WITH _accessible AS (
              ${accessible}
            ),
            _names AS (
              ${tablenames
            .map((x) => `SELECT '${x}'::varchar AS name`)
            .join('\nUNION ALL\n')}
            )
            SELECT name AS noAccess
            FROM _names n
            WHERE NOT EXISTS (
              SELECT 1
              FROM _accessible a
              WHERE n.name = a.table_name
            );`;
        return query;
    }
    getTempStorageSetupQueries(options) {
        const [database, schema] = this.tempStoragePath.split('.');
        const procName = `_temp_proc_${hashString(this.tempStoragePath)}`;
        const proc = dedent `
      DECLARE
        schemaExists BOOL;
      BEGIN
        SELECT COUNT(*) != 0 INTO schemaExists FROM pg_namespace WHERE nspname = '${schema}';
        IF (schemaExists) THEN
          BEGIN
            EXECUTE 'CREATE TABLE ${schema}.__testtable AS (SELECT 1 AS N);';
            EXECUTE 'DROP TABLE IF EXISTS ${schema}.__testtable;';
            SELECT '{"success": true}' INTO result;
          EXCEPTION WHEN OTHERS THEN
            SELECT '{"success": false, "error": "temp_storage_create_tables"}' INTO result;
          END;
        ELSE
          BEGIN
            CREATE SCHEMA IF NOT EXISTS ${schema};
            SELECT '{"success": true}' INTO result;
          EXCEPTION WHEN OTHERS THEN
            SELECT '{"success": false, "error": "temp_storage_access_create"}' INTO result;
          END;
        END IF;
      END;`;
        return [
            dedent `CREATE OR REPLACE PROCEDURE ${procName}(result OUT VARCHAR(MAX))
       AS $$
         ${proc}
       $$ LANGUAGE plpgsql;`,
            `CALL ${procName}();`,
            `DROP PROCEDURE ${procName}();`
        ];
    }
    getStoredProcedureCacheInvalidationQueries(workflow) {
        const procName = `_invalidate_stored_proc_cache_${hashString(this.tempStoragePath)}`;
        const proc = dedent `
    DECLARE
      statement RECORD;
      query VARCHAR(MAX);
    BEGIN
      query := 'SELECT
          ''DROP TABLE ${this.tempStoragePath}.'' || relname || '';'' as statement
        FROM
          pg_class_info
        LEFT JOIN
          pg_namespace
        ON
          pg_class_info.relnamespace = pg_namespace.oid
        WHERE
          reltype != 0
          AND relname ILIKE ''${workflow.getStoredProcedureOutputTableName(this)}%''
      ';
      FOR statement IN EXECUTE query
      LOOP
        EXECUTE statement.statement;
      END LOOP;
    END;
    `;
        return [
            dedent `CREATE OR REPLACE PROCEDURE ${procName}()
       AS $$
         ${proc}
       $$ LANGUAGE plpgsql;`,
            `CALL ${procName}();`,
            `DROP PROCEDURE ${procName}();`
        ];
    }
    getTableListQuery(filter) {
        const [database, schema] = this.tempStoragePath.split('.');
        return dedent `
      SELECT table_name
      FROM "${database}".information_schema.tables
      WHERE table_schema = '${schema}' AND table_name LIKE '${filter}';`;
    }
    dataProviderTypeToColumnType(type) {
        switch (type) {
            case 'INT2':
            case 'INT4':
            case 'INT8':
            case 'FLOAT4':
            case 'FLOAT8':
            case 'NUMERIC':
                return ColumnDataType.Number;
            case 'BOOL':
                return ColumnDataType.Boolean;
            case 'GEOMETRY':
                return ColumnDataType.Geometry;
            case 'GEOGRAPHY':
                return ColumnDataType.Geography;
            case 'DATE':
                return ColumnDataType.Date;
            case 'TIME':
            case 'TIMETZ':
                return ColumnDataType.Time;
            case 'TIMESTAMP':
            case 'TIMESTAMPTZ':
                return ColumnDataType.Timestamp;
            default:
                return ColumnDataType.String;
        }
    }
    quoteName(name) {
        if (name) {
            const parts = name.split('.');
            if (parts.length === 3) {
                name = `"${parts[0].replace(/"/g, '')}".${parts[1]}.${parts[2]}`;
            }
        }
        return name;
    }
    executeDynamicSQL({ sql, addQuotes, replacements }) {
        const lines = sql
            .replace(/BEGIN\n([\s\S]*?)END;/, '$1')
            .split(';')
            .filter((s) => /\S/.test(s));
        if (replacements) {
            return lines
                .map((q) => dedent `EXECUTE
                          ${replacements?.reduce((acc, [org, repl]) => dedent `REPLACE(
                                ${acc},
                                ${org},
                                ${repl}
                              )
                              `, addQuotes
                ? this.quoteMultilineString(dedent(q))
                : dedent(q))};`)
                .join('\n');
        }
        else {
            return lines
                .map((q) => dedent `EXECUTE
                          ${addQuotes
                ? this.quoteMultilineString(dedent(q))
                : dedent(q)}
                        ;`)
                .join('\n');
        }
    }
    applyCapitalization(tablename) {
        return tablename.toLowerCase();
    }
    adaptComponent(component) {
        const whiteList = ['native.geographytogeometry'];
        if (!whiteList.includes(component.metadata.name)) {
            for (const input of component.inputs) {
                if (input.type === WorkflowComponentParamType.Column &&
                    input.dataType) {
                    input.dataType = input.dataType.filter((x) => x !== ColumnDataType.Geography);
                }
            }
        }
        component.inputs = component.inputs.filter((x) => x.providers == null || x.providers.includes(Provider.Redshift));
    }
    getOutputSchemasQueries(tableNames) {
        const parts = this.tempStoragePath.split('.');
        const schema = parts[1];
        return dedent `
      WITH cols AS (
        SELECT
          table_name,
          column_name AS name,
          data_type AS db_type,
          ordinal_position
        FROM svv_columns
        WHERE
          table_name IN (${tableNames.map((t) => `'${t}'`).join(', ')})
          AND table_schema = '${schema}'
      ),
      cols_with_types AS (
        SELECT
          *,
          CASE LOWER(db_type)
            WHEN 'int2' THEN '${ColumnDataType.Number}'
            WHEN 'int4' THEN '${ColumnDataType.Number}'
            WHEN 'int8' THEN '${ColumnDataType.Number}'
            WHEN 'float4' THEN '${ColumnDataType.Number}'
            WHEN 'float8' THEN '${ColumnDataType.Number}'
            WHEN 'numeric' THEN '${ColumnDataType.Number}'
            WHEN 'bool' THEN '${ColumnDataType.Boolean}'
            WHEN 'geometry' THEN '${ColumnDataType.Geometry}'
            WHEN 'geography' THEN '${ColumnDataType.Geography}'
            WHEN 'date' THEN '${ColumnDataType.Date}'
            WHEN 'time' THEN '${ColumnDataType.Time}'
            WHEN 'timetz' THEN '${ColumnDataType.Time}'
            WHEN 'timestamp' THEN '${ColumnDataType.Timestamp}'
            WHEN 'timestamptz' THEN '${ColumnDataType.Timestamp}'
            ELSE '${ColumnDataType.String}'
          END AS type
        FROM cols
      )
      SELECT
        CONCAT('${this.tempStoragePath}.', table_name) AS fqn,
        '[' ||
          LISTAGG(
            '{"name":"' || name || '","type":"' || type || '","db_type":"' || db_type || '"}',
            ','
          )
          WITHIN GROUP (ORDER BY ordinal_position ASC)
        || ']'
        AS schema
      FROM cols_with_types
      GROUP BY table_name;`;
    }
    prepareSchemaQueries(queries) {
        const last = queries.pop();
        const procName = `_temp_${hashString(JSON.stringify(queries))}`;
        return [
            dedent `
        CREATE OR REPLACE PROCEDURE ${procName}()
        AS $$
        BEGIN
          ${queries.join('\n')}
        END;
        $$ LANGUAGE plpgsql;`,
            `CALL ${procName}();`,
            `DROP PROCEDURE ${procName}();`,
            last
        ];
    }
    getGeoDataType() {
        return ColumnDataType.Geometry;
    }
}
